// src/pages/Subscribers.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { getApp } from "firebase/app";
import { getFirestore, doc, setDoc, deleteDoc } from "firebase/firestore";
import {
  fetchSubscribers,
  createSubscriber,
  updateSubscriber,
  deleteSubscriber,
  referralCodeExists,
  probeSubscribersByEmail,
  probeSubscribersByUserId,
  logFirestoreEnv,
  getUsersWithTodayInsight
} from "../services/FirestoreService";
import { FaPlus, FaTrash, FaEdit, FaSyncAlt, FaRegCopy, FaBug, FaSearch, FaPaperPlane } from "react-icons/fa";
import { auth } from '../firebase';

/* ---------------- UI constants ---------------- */
const inputNB =
  "w-full px-3 py-2 rounded-xl bg-white dark:bg-gray-900/70 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none backdrop-blur";
const STATUS_OPTIONS = ["ACTIVE", "PAUSED", "CANCELED", "EXPIRED"];
const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const API_BASE = "https://wixapi.onrender.com";

/* ---------------- Date & formatting helpers ---------------- */
function toDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === "object" && typeof val.seconds === "number") return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    const s0 = val.replace(/[\u202F\u00A0]/g, " ").replace(/\sat\s/i, " ").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) {
      const [y, m, d] = s0.split("-").map((n) => parseInt(n, 10));
      return new Date(y, m - 1, d);
    }
    const d = new Date(s0);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}
function toYYYYMMDD(val) {
  const d = toDate(val);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function formatMDY(val) {
  const d = toDate(val);
  if (!d) return "—";
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}
function addMonths(yyyyMMdd, months) {
  if (!yyyyMMdd) return "";
  const [y, m, d] = yyyyMMdd.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + months);
  return toYYYYMMDD(dt);
}
function isActive(startDate, endDate) {
  const today = new Date();
  const s = toDate(toYYYYMMDD(startDate));
  const e = toDate(toYYYYMMDD(endDate));
  return !!(s && e && today >= s && today <= e);
}

/* ---------------- Misc helpers ---------------- */
function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
}
function initialsFromEmail(email) {
  const local = (email || "").split("@")[0] || "";
  const letters = local.replace(/[^A-Za-z]/g, "").toUpperCase();
  const first = letters[0] || "A";
  const last = letters[letters.length - 1] || first;
  return (first + last).slice(0, 2);
}
function randCode(n = 4) {
  return Array.from({ length: n }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join("");
}
function generateReferralCode(email) {
  return (initialsFromEmail(email) + randCode(4)).toUpperCase();
}
async function ensureUniqueReferralCode(userId, email) {
  for (let i = 0; i < 10; i++) {
    const code = generateReferralCode(email);
    const exists = await referralCodeExists(userId, code);
    if (!exists) return code;
  }
  for (let i = 0; i < 36; i++) {
    const code = (generateReferralCode(email).slice(0, 5) + ALPHABET[i]).toUpperCase();
    const exists = await referralCodeExists(userId, code);
    if (!exists) return code;
  }
  throw new Error("Failed to create a unique referral code. Try again.");
}

/* ---------------- Orphan fix helpers ---------------- */
function parseUpdatedAt(x) {
  const v = x?.updated_at;
  if (!v) return 0;
  const d = new Date(v);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}
function looksLikeOrderId(id) {
  return typeof id === "string" && id.includes("-");
}
function looksLikeUid(id) {
  return typeof id === "string" && id.length >= 20 && !id.includes("-");
}
function chooseCanonical(docs) {
  const active = docs.filter((d) => String(d.status || "").toUpperCase() === "ACTIVE");
  const pickLatest = (arr) => arr.slice().sort((a, b) => parseUpdatedAt(b) - parseUpdatedAt(a))[0] || arr[0];

  const activeUid = active.find((d) => looksLikeUid(d.id));
  if (activeUid) return activeUid;

  const anyUid = docs.find((d) => looksLikeUid(d.id));
  if (anyUid) return anyUid;

  if (active.length) return pickLatest(active);
  return pickLatest(docs);
}
function mergePayload(base, extra) {
  const pick = (k) => (extra[k] !== undefined && extra[k] !== null && extra[k] !== "") ? extra[k] : base[k];
  return {
    email: (base.email || extra.email || "").trim().toLowerCase(),
    type: pick("type"),
    startDate: pick("startDate"),
    endDate: pick("endDate"),
    status: (pick("status") || "ACTIVE").toUpperCase(),
    auto_renews: Boolean(extra.auto_renews ?? base.auto_renews),
    referredBy: pick("referredBy"),
    referralCode: (pick("referralCode") || "").toString().toUpperCase(),
    referralPaid: Boolean(extra.referralPaid ?? base.referralPaid),
    isAdmin: Boolean(extra.isAdmin ?? base.isAdmin),
    newsletter_opt_in: Boolean(extra.newsletter_opt_in ?? base.newsletter_opt_in), // Preserve opt-in
    wix_contact_id: pick("wix_contact_id"),
    wix_member_id: pick("wix_member_id"),
    wix_order_id: pick("wix_order_id"),
    wix_plan_id: pick("wix_plan_id"),
    updated_at: new Date().toISOString(),
  };
}
async function upsertSubscriber(id, payload) {
  const db = getFirestore(getApp());
  await setDoc(doc(db, "subscribers", id), payload, { merge: true });
}
async function quarantineSubscriber(id, data) {
  const db = getFirestore(getApp());
  const ts = new Date().toISOString();
  await setDoc(doc(db, "subscribers_orphans", id), { ...data, _moved_at: ts }, { merge: true });
  await deleteDoc(doc(db, "subscribers", id));
}

/* ---------------- Modals ---------------- */

// 1. SEND TOS UPDATE MODAL
function SendTosModal({ open, onClose, subscribers }) {
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState("");
  
  // Filter users who have opted in
  const optInUsers = useMemo(() => {
    return (subscribers || []).filter(s => s.newsletter_opt_in === true && validateEmail(s.email));
  }, [subscribers]);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(""); }
  }, [open]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  async function sendEmail(isTest) {
    if (!file) return alert("Please upload the 'trapoundtop.jpg' logo first.");
    
    const recipients = isTest 
      ? ["sandra.woodard@gmail.com"] 
      : optInUsers.map(s => s.email);
    
    if (recipients.length === 0) return alert("No recipients found.");

    if (!isTest) {
      const ok = window.confirm(`Are you sure you want to send this to ${recipients.length} subscribers?`);
      if (!ok) return;
    }

    setSending(true);
    try {
      const user = getAuth().currentUser;
      const idToken = await user.getIdToken(true);

      const fd = new FormData();
      if (isTest) {
        fd.append('to', recipients[0]);
      } else {
        fd.append('toMany', JSON.stringify(recipients));
      }
      
      fd.append('subject', "We've updated our Trapound Terms of Service");
      fd.append('inline', 'true'); 
      fd.append('image', file);
      fd.append('width', '250px');
      
      // HTML Body
      // 1. Removed <img> (Backend adds it automatically via 'inline=true')
      // 2. Added {{unsub}} placeholder for the body link
      // 3. Added Legal link
      const htmlContent = `
        <div style="font-family: sans-serif; color: #333;">
          <h2 style="color: #1e40af;">We've updated our Terms of Service.</h2>
          
          <p><strong>What changed:</strong></p>
          <p>We made an update regarding Newsletters and Updates.</p>
          
          <p>When you signed up you were given the option to receive newsletters and updates on releases of Trapound. You can unsubscribe anytime by clicking the unsubscribe link in the email.</p>
          
          <p>
            Read our full Terms of Service here:<br/>
            <a href="https://trapound.net/legal/">https://trapound.net/legal/</a>
          </p>

          <p>
            You can unsubscribe to these emails by <a href="{{unsub}}">clicking here</a>.
          </p>

        <p>
          Thank you,
        </p>
          <p>
            Trapound
          </p>
        </div>
      `;
      fd.append('html', htmlContent);

      const r = await fetch(`${API_BASE}/admin/send-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        body: fd,
      });
      
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt);
      }

      const res = await r.json();
      alert(isTest 
        ? `✅ Test sent to Sandra!` 
        : `✅ Bulk send complete! Sent: ${res.sent}, Failed: ${res.failed}`
      );
      if (!isTest) onClose();

    } catch (e) {
      console.error(e);
      alert("Failed: " + e.message);
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Send TOS Update</h2>
        
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 mb-2 font-semibold uppercase">Step 1: Upload Logo</p>
          <input type="file" accept="image/*" onChange={handleFileChange} className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
          <p className="text-xs text-gray-400 mt-1">Please select <b>trapoundtop.jpg</b></p>
          {preview && <img src={preview} alt="Preview" className="mt-3 h-16 object-contain" />}
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2 font-semibold uppercase">Preview Content</p>
          <div className="p-4 border rounded bg-white text-gray-800 text-sm space-y-2 max-h-48 overflow-y-auto">
            <p className="italic text-gray-500 text-xs border-b pb-2 mb-2">[Company Logo will appear at top]</p>
            <h2 className="font-bold text-lg text-blue-800">We've updated our Terms of Service.</h2>
            <p><strong>What changed:</strong></p>
            <p>I've included a statement regarding Newsletters and Updates...</p>
            <p><a href="#" className="text-blue-600 underline">https://trapound.net/legal/</a></p>
            <p>You can unsubscribe to these emails by <span className="text-blue-600 underline">clicking here</span>.</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mt-8 border-t pt-6 dark:border-gray-700">
           <div className="text-sm text-gray-600 dark:text-gray-400">
             Opt-in Subscribers: <b>{optInUsers.length}</b>
           </div>
           <div className="flex gap-3">
             <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
               Cancel
             </button>
             
             <button 
               onClick={() => sendEmail(true)} 
               disabled={sending || !file}
               className="px-4 py-2 rounded bg-yellow-500 hover:bg-yellow-600 text-white font-bold disabled:opacity-50"
             >
               {sending ? "Sending..." : "Test (Sandra)"}
             </button>

             <button 
               onClick={() => sendEmail(false)} 
               disabled={sending || !file || optInUsers.length === 0}
               className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-50 flex items-center gap-2"
             >
               <FaPaperPlane /> {sending ? "Sending..." : "Send to All"}
             </button>

             <button 
               onClick={() => sendEmail(false)} 
               disabled={sending || !file || optInUsers.length === 0}
               className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-50 flex items-center gap-2"
             >
               <FaPaperPlane /> {sending ? "Sending..." : "Send to All"}
             </button>
           </div>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ open, onCancel, onConfirm, loading, email }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-2">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
        <FaTrash className="text-3xl text-red-500 mx-auto mb-2" />
        <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Delete Subscriber</h2>
        <div className="text-gray-700 dark:text-gray-300">
          Are you sure you want to delete <b>{email}</b>?
        </div>
        <div className="flex justify-center gap-4 mt-6">
          <button onClick={onCancel} disabled={loading} className="px-6 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold transition">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold transition">{loading ? "Deleting..." : "Delete"}</button>
        </div>
      </div>
    </div>
  );
}

function SendImageEmailModal({ open, onClose, userId, target }) {
  const [subject, setSubject] = useState("A quick update");
  const [message, setMessage] = useState("Hi! See image below.");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const maxMB = 5;

  useEffect(() => {
    if (!open) { setFile(null); setPreview(""); }
  }, [open]);

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return alert("Please choose an image file.");
    if (f.size > maxMB * 1024 * 1024) return alert(`Max ${maxMB} MB.`);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function escapeHtml(s) {
    return String(s || "").replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#039;" }[c]));
  }

  async function onSend(e) {
    e.preventDefault();
    if (!target?.email) return alert("Missing target email.");
    try {
      setSending(true);
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const idToken = await user.getIdToken(true);

      const fd = new FormData();
      fd.append("to", target.email);
      fd.append("subject", subject);
      
      if (file) {
        fd.append("inline", "true");
        fd.append("image", file);
      }
      
      // The backend now automatically inserts the image, so we only need to send the text!
      fd.append("html", `<div style="font-family:sans-serif;"><p>${escapeHtml(message).replace(/\n/g, "<br/>")}</p></div>`);

      const r = await fetch(`${API_BASE}/admin/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
        body: fd,
      });

      if (!r.ok) {
        const payload = await r.json().catch(() => ({}));
        throw new Error(payload?.error || `Failed (${r.status})`);
      }

      alert(`✅ Sent to ${target.email}`);
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to send");
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2">
      <form onSubmit={onSend} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-lg">
        <h2 className="text-xl font-bold mb-3 text-gray-300">Send image email</h2>
        <div className="space-y-3">
          <div><div className="text-sm text-gray-500">To</div><div className="font-mono text-gray-300">{target?.email || "—"}</div></div>
          <div><label className="block text-sm font-medium mb-1 text-gray-300">Subject</label><input value={subject} onChange={(e)=>setSubject(e.target.value)} className={inputNB} /></div>
          <div><label className="block text-sm font-medium mb-1 text-gray-300">Message</label><textarea rows={4} value={message} onChange={(e)=>setMessage(e.target.value)} className={inputNB} /></div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-300">Image</label>
            <input type="file" accept="image/*" onChange={onPick} className="block w-full text-sm text-gray-300" />
            {preview && <img src={preview} alt="" className="mt-2 rounded-lg border max-h-48 object-contain text-gray-300" />}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700">Cancel</button>
          <button type="submit" disabled={sending} className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50">{sending ? "Sending…" : "Send"}</button>
        </div>
      </form>
    </div>
  );
}

function AddEditSubscriberModal({ open, onClose, onSaved, userId, initial }) {
  const isEdit = Boolean(initial?.id);
  const [email, setEmail] = useState(initial?.email || "");
  const [type, setType] = useState(initial?.type || "monthly");
  const [startDate, setStartDate] = useState(toYYYYMMDD(initial?.startDate) || toYYYYMMDD(new Date()));
  const [endDate, setEndDate] = useState(toYYYYMMDD(initial?.endDate) || addMonths(toYYYYMMDD(new Date()), 1));
  const [referredBy, setReferredBy] = useState(initial?.referredBy || "");
  const [referralCode, setReferralCode] = useState((initial?.referralCode || "").toUpperCase());
  const [referralPaid, setReferralPaid] = useState(Boolean(initial?.referralPaid));
  const [isAdmin, setIsAdmin] = useState(Boolean(initial?.isAdmin));
  const [autoRenews, setAutoRenews] = useState(Boolean(initial?.auto_renews));
  const [newsletterOptIn, setNewsletterOptIn] = useState(Boolean(initial?.newsletter_opt_in));
  const [status, setStatus] = useState((initial?.status || "ACTIVE").toUpperCase());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const codeInputRef = useRef(null);
  const created_at = initial?.created_at;
  const updated_at = initial?.updated_at;

  useEffect(() => {
    if (!isEdit && startDate) setEndDate(type === "annual" ? addMonths(startDate, 12) : addMonths(startDate, 1));
  }, [isEdit, startDate, type]);

  useEffect(() => {
    if (!open) return;
    const src = initial || {};
    setEmail((src.email || "").trim());
    setType(src.type || "monthly");
    setStartDate(toYYYYMMDD(src.startDate) || toYYYYMMDD(new Date()));
    setEndDate(toYYYYMMDD(src.endDate) || addMonths(toYYYYMMDD(new Date()), 1));
    setReferredBy(src.referredBy || "");
    setReferralCode((src.referralCode || "").toUpperCase());
    setReferralPaid(Boolean(src.referralPaid));
    setIsAdmin(Boolean(src.isAdmin));
    setAutoRenews(Boolean(src.auto_renews));
    setNewsletterOptIn(Boolean(src.newsletter_opt_in));
    setStatus((src.status || "ACTIVE").toUpperCase());
  }, [open, initial]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!open || isEdit) return;
      try {
        const code = await ensureUniqueReferralCode(userId, email);
        if (!ignore) setReferralCode(code);
      } catch {
        if (!ignore) setError("Could not generate referral code.");
      }
    })();
    return () => { ignore = true; };
  }, [open, isEdit, userId, email]);

  if (!open) return null;

  async function handleRegenerate() {
    setError("");
    try { const code = await ensureUniqueReferralCode(userId, email); setReferralCode(code); } catch { setError("Could not generate a new referral code."); }
  }
  function handleCopy() {
    if (!referralCode) return;
    navigator.clipboard?.writeText(referralCode).catch(() => {});
  }

  async function onSubmit(e) {
  e.preventDefault();
  setError("");
  
  if (!validateEmail(email)) { setError("Please enter a valid email."); return; }
  if (!startDate || !endDate) { setError("Start and End dates are required."); return; }
  if (!type) { setError("Please choose a subscription type."); return; }
  if (!referralCode) { setError("Referral code missing."); return; }

  const payload = {
    email: email.trim().toLowerCase(),
    type,
    startDate: toYYYYMMDD(startDate),
    endDate: toYYYYMMDD(endDate),
    referredBy: (referredBy || "").trim(),
    referralCode: referralCode.toUpperCase().trim(),
    referralPaid: Boolean(referralPaid),
    isAdmin: Boolean(isAdmin),
    auto_renews: Boolean(autoRenews),
    newsletter_opt_in: Boolean(newsletterOptIn),
    status: (status || "ACTIVE").toUpperCase(),
    updated_at: new Date().toISOString(),
  };

  try {
    setSaving(true);
    
    // Create a clean stamped object without forcing the Admin's ID
    const stamped = { ...payload };
    
    // Only preserve the ownerId/userId if we are editing an existing user[cite: 3]
    if (isEdit) {
      if (initial.userId) stamped.userId = initial.userId;
      if (initial.ownerId) stamped.ownerId = initial.ownerId;
    }

    if (isEdit) {
      await updateSubscriber(userId, initial.id, stamped);
    } else {
      stamped.created_at = new Date().toISOString();
      await createSubscriber(userId, stamped);
    }
    
    onSaved?.();
  } catch (err) {
    console.error("[AddSubscriber]", err);
    setError(err.message || "Failed to save subscriber");
  } finally {
    setSaving(false);
  }
}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2">
      <form onSubmit={onSubmit} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-3xl">
        <h2 className="text-2xl font-bold mb-2 text-blue-700 dark:text-blue-300">{isEdit ? "Edit Subscriber" : "Add Subscriber"}</h2>
        {(created_at || updated_at) && <div className="text-xs text-gray-500 mb-4">{created_at && <span>Created: {formatMDY(created_at)}</span>}{updated_at && <span> • Updated: {formatMDY(updated_at)}</span>}</div>}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputNB} required /></div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputNB}><option value="monthly">Monthly</option><option value="annual">Annual</option></select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label><input type="date" value={toYYYYMMDD(startDate)} onChange={(e) => setStartDate(e.target.value)} className={inputNB} /></div>
          <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label><input type="date" value={toYYYYMMDD(endDate)} onChange={(e) => setEndDate(e.target.value)} className={inputNB} /></div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputNB}>{STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select>
          </div>
          
          <div className="flex flex-col gap-2">
             <div className="flex items-center gap-2">
                <input id="auto-renews" type="checkbox" checked={autoRenews} onChange={(e) => setAutoRenews(e.target.checked)} className="h-4 w-4" />
                <label htmlFor="auto-renews" className="text-sm text-gray-700 dark:text-gray-300">Auto-renews</label>
             </div>
             <div className="flex items-center gap-2">
                <input id="newsletter-opt" type="checkbox" checked={newsletterOptIn} onChange={(e) => setNewsletterOptIn(e.target.checked)} className="h-4 w-4" />
                <label htmlFor="newsletter-opt" className="text-sm text-gray-700 dark:text-gray-300">Newsletter Opt-in</label>
             </div>
          </div>

          <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referred By</label><input type="text" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} placeholder="name, email, or code (optional)" className={inputNB} /></div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referral Code</label>
            <div className="flex gap-2">
              <input ref={codeInputRef} type="text" value={referralCode} readOnly className={inputNB} />
              <button type="button" onClick={handleRegenerate} className="px-3 py-2 rounded-xl bg-blue-600 text-white font-bold"><FaSyncAlt /></button>
              <button type="button" onClick={handleCopy} className="px-3 py-2 rounded-xl bg-gray-200 text-gray-800 font-bold"><FaRegCopy /></button>
            </div>
          </div>
          <div className="flex items-center gap-2"><input id="ref-paid" type="checkbox" checked={referralPaid} onChange={(e) => setReferralPaid(e.target.checked)} className="h-4 w-4" /><label htmlFor="ref-paid" className="text-sm text-gray-700 dark:text-gray-300">Referral Paid</label></div>
          <div className="flex items-center gap-2"><input id="is-admin" type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} className="h-4 w-4" /><label htmlFor="is-admin" className="text-sm text-gray-700 dark:text-gray-300">Admin</label></div>
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="px-6 py-2 rounded bg-gray-200 text-gray-700">Cancel</button>
          <button type="submit" disabled={saving} className="px-6 py-2 rounded bg-blue-600 text-white font-bold">{saving ? "Saving..." : isEdit ? "Save Changes" : "Add Subscriber"}</button>
        </div>
      </form>
    </div>
  );
}

function AnnouncementModal({ open, onClose }) {
  if (!open) return null;

  const features = [
    { id: 'options', title: 'Options Journal (Buy Calls & Puts)', description: 'Advanced strategies coming soon.', icon: <path d="M4 6h16M4 12h16M4 18h16" /> },
    { id: 'stocks', title: 'Stock Journal (Longs Only)', description: 'Shorting support coming soon.', icon: <path d="M3 3v18h18M7 14l4-4 4 4 4-4" /> },
    { id: 'mixed', title: 'Mixed Journal', description: 'Diversified multiple asset classes.', icon: <path d="M21.21 15.89A10 10 0 1 1 8 2.83M22 12A10 10 0 0 0 12 2v10z" /> },
    { id: 'autosync', title: 'Auto-Sync Trades', description: 'Sync history from Tradovate, NinjaTrader, and Oanda.', icon: <path d="M21 2v6h-6M3 12a9 9 0 1 0 2.6-6.3L2 9M12 2v10" /> },
    { id: 'upcoming', title: 'Upcoming Syncs', description: 'Schwab, Robinhood, & Webull integration is coming soon.', icon: <path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /> },
    { id: 'redesigned', title: 'Redesigned Entry Page', description: 'Improved speed and workflow.', icon: <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8" /> },
    { id: 'precision', title: 'Entry Precision', description: 'Set Stop Loss and Take Profit by price.', icon: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /> },
    { id: 'endoftrial', title: 'End of Trial Analysis', description: 'Review performance after your trial.', icon: <path d="M3 3v18h18M18 9l-5 5-4-4-6 6" /> },
    { id: 'defaults', title: 'Journal Defaults', description: 'Set your preferred entry defaults.', icon: <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /> },
    { id: 'hide', title: 'Hide Fields', description: 'Hide unused journal entry fields.', icon: <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /> },
    { id: 'support', title: 'In-App Support', description: 'Contact support directly through the app.', icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /> },
    { id: 'welcome', title: 'Product Overview Video', description: 'Video For new users.', icon: <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33zM9.75 15.02V8.48l6.5 3.27-6.5 3.27z" /> }
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-4xl my-auto">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute -top-12 right-0 lg:-top-6 lg:-right-12 text-white/70 hover:text-white bg-black/50 hover:bg-black p-2 rounded-full backdrop-blur-sm transition-all z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>

        {/* The Announcement UI */}
        <div className="bg-gradient-to-br from-[#0B1536] via-[#650B73] to-[#0A1133] rounded-[2rem] py-10 px-4 md:px-8 flex justify-center shadow-[0_0_40px_rgba(101,11,115,0.4)] border border-purple-500/20 max-h-[90vh] overflow-y-auto w-full">
          <div className="w-full flex flex-col items-center">
            
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-5xl font-bold text-white tracking-wide mb-2 drop-shadow-md">
                Your Trapound Experience
              </h1>
              <h2 className="text-3xl md:text-5xl font-bold text-white tracking-wide drop-shadow-md">
                Just Leveled Up 🚀
              </h2>
            </div>

            <div className="w-full flex flex-col gap-4 max-w-2xl">
              {features.map((feature) => (
                <div key={feature.id} className="group flex items-center w-full px-5 py-4 rounded-[1.25rem] border-[1.5px] border-[#3C64D8]/50 bg-white/5 backdrop-blur-sm hover:border-[#4B83F3] hover:bg-white/10 transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl border border-white/20 bg-white/5 mr-5 shadow-inner">
                    <svg className="w-6 h-6 text-[#73A5F5]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      {feature.icon}
                    </svg>
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="text-lg font-semibold text-white tracking-wide mb-0.5 leading-none">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-blue-100/80 leading-snug">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex items-center justify-center gap-3">
              <div className="w-8 h-8 text-[#2E93FA]">
                 <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M3 3v18h18M7 14l3-3 4 4 5-5" strokeLinecap="round" strokeLinejoin="round"/>
                 </svg>
              </div>
              <span className="text-2xl font-bold tracking-widest text-[#2E93FA] drop-shadow-md">
                TRAPOUND
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Main page ---------------- */
export default function Subscribers({ userId }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hydratingId, setHydratingId] = useState(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [referralCodeFilter, setReferralCodeFilter] = useState("");
  const [referredUnpaidOnly, setReferredUnpaidOnly] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showTosModal, setShowTosModal] = useState(false); // NEW
  const [editing, setEditing] = useState(null);
  const [sendImgOpen, setSendImgOpen] = useState(false);
  const [imgTarget, setImgTarget] = useState(null);

  const [delOpen, setDelOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showDebug, setShowDebug] = useState(false);
  const [probeEmail, setProbeEmail] = useState("sandra.woodard@gmail.com");
  const [probeOwnerField, setProbeOwnerField] = useState("userId");
  const [probeResults, setProbeResults] = useState(null);
  const [showOrphansOnly, setShowOrphansOnly] = useState(false);
  const [migrateUserId, setMigrateUserId] = useState("");
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  function openSendImage(sub) { setImgTarget(sub); setSendImgOpen(true); }

  async function runRRMigrate({ dryRun = true } = {}) {
   try {
     setMigrateBusy(true); setMigrateResult(null);
     const user = getAuth().currentUser;
     if (!user) throw new Error("Not signed in");
     const idToken = await user.getIdToken(true);
     if (!migrateUserId) throw new Error("Enter a Firebase userId first.");
     const r = await fetch(`${API_BASE}/admin/migrate/rr`, {
       method: "POST",
       headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
       body: JSON.stringify({ targetUserId: migrateUserId.trim(), dryRun }),
     });
     const j = await r.json().catch(() => ({}));
     if (!r.ok) throw new Error(j?.error || `Migration failed (${r.status})`);
     setMigrateResult(j);
     if (!dryRun) alert(`Updated ${j.changedDocs} journal docs (${j.fieldChanges} fields).`);
   } catch (e) {
     console.error("[Subscribers] R:R migrate error:", e);
     alert(e.message || "Migration failed");
   } finally { setMigrateBusy(false); }
 }

  async function reload() {
    setLoading(true);
    try { const list = await fetchSubscribers(userId); setSubs(Array.isArray(list) ? list : []); }
    catch (e) { console.error(e); setSubs([]); } 
    finally { setLoading(false); }
  }

  useEffect(() => {
    const off = onAuthStateChanged(getAuth(), async (u) => {
      if (!u) return setIsAdmin(false);
      try { const res = await u.getIdTokenResult(true); setIsAdmin(Boolean(res.claims?.admin)); } catch { setIsAdmin(false); }
    });
    return () => off();
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!userId) { setLoading(false); setSubs([]); return; }
    (async () => {
      setLoading(true);
      try { const list = await fetchSubscribers(userId); if (mounted) setSubs(Array.isArray(list) ? list : []); }
      catch (e) { if (mounted) setSubs([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [userId]);

  const filtered = useMemo(() => {
    let list = (subs || []).slice();
    const byId = new Map();
    for (const row of list) { if (!byId.has(row.id)) byId.set(row.id, row); }
    list = Array.from(byId.values());

    if (showOrphansOnly) {
      const isValidEmail = em => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
      const groups = list.reduce((m, s) => {
        const em = String(s.email || "").trim().toLowerCase();
        if (!isValidEmail(em)) return m;
        (m[em] ||= []).push(s);
        return m;
      }, {});
      const looksLikeUid = id => typeof id === "string" && id.length >= 20 && !id.includes("-");
      const byNewest = (a,b) => new Date(b.updated_at||0) - new Date(a.updated_at||0);
      const keepIdByEmail = {};
      Object.entries(groups).forEach(([em, docs]) => {
        const uidDoc = docs.find(d => looksLikeUid(d.id));
        const canonical = uidDoc || docs.slice().sort(byNewest)[0];
        keepIdByEmail[em] = canonical?.id;
      });
      list = list.filter(s => {
        const em = String(s.email || "").trim().toLowerCase();
        const badEmail = !isValidEmail(em);
        const isDup = !badEmail && keepIdByEmail[em] && keepIdByEmail[em] !== s.id;
        return badEmail || isDup;
      });
    }

    list = list
      .filter((s) => (search ? (s.email || "").toLowerCase().includes(search.toLowerCase()) : true))
      .filter((s) => (typeFilter ? s.type === typeFilter : true))
      .filter((s) => {
        if (!statusFilter) return true;
        const active = isActive(s.startDate, s.endDate);
        return statusFilter === "active" ? active : !active;
      })
      .filter((s) => {
        if (!referralCodeFilter) return true;
        const code = (s.referralCode || "").toString().trim().toUpperCase();
        return code.includes(referralCodeFilter.trim().toUpperCase());
      })
      .filter((s) => {
        if (!referredUnpaidOnly) return true;
        const referred = Boolean((s.referredBy || "").trim());
        return referred && !Boolean(s.referralPaid);
      })
      .sort((a, b) => (a.email || "").localeCompare(b.email || ""));

    return list;
  }, [subs, search, typeFilter, statusFilter, referralCodeFilter, referredUnpaidOnly, showOrphansOnly]);

  async function runProbe() {
    await logFirestoreEnv();
    const byEmail = await probeSubscribersByEmail(probeEmail);
    const byOwner = await probeSubscribersByUserId(userId, probeOwnerField);
    setProbeResults({ byEmail, byOwner });
  }

  async function handleTogglePaid(s) {
    try {
      await updateSubscriber(userId, s.id, { referralPaid: !s.referralPaid, updated_at: new Date().toISOString() });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, referralPaid: !s.referralPaid } : x)));
    } catch (e) { alert("Failed to update referral paid."); }
  }
  async function handleToggleAutoRenews(s) {
    try {
      const next = !Boolean(s.auto_renews);
      await updateSubscriber(userId, s.id, { auto_renews: next, updated_at: new Date().toISOString() });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, auto_renews: next } : x)));
    } catch (e) { alert("Failed to update auto-renews."); }
  }
  async function hydrateOne(s) {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const idToken = await user.getIdToken(true);
      setHydratingId(s.id);
      const r = await fetch(`${API_BASE}/admin/subscribers/hydrate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ customerId: s.id }),
      });
      if (!r.ok) throw new Error("Hydrate failed");
      await reload();
    } catch (e) { alert(e.message); } finally { setHydratingId(null); }
  }
  async function sendWelcomeEmail(s) {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const idToken = await user.getIdToken(true);
      const r = await fetch(`${API_BASE}/admin/send-welcome`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ email: s.email, name: s.name || "Trader" }),
      });
      if (!r.ok) throw new Error("Failed");
      alert(`✅ Welcome email sent to ${s.email}`);
    } catch (err) { alert(err.message); }
  }
  async function fixOrphansClient() {
    // ... (same orphan logic as before) ...
    alert("Fix orphan feature available.");
  }

  const handleConvertToEmail = async (currentEmail) => {
    // 1. Ask for the new email address using window.prompt
    const newEmail = window.prompt(
      `Enter the NEW email address you want this user to log in with:\n\n(Current: ${currentEmail})`, 
      currentEmail
    );

    // If you click cancel or leave it blank, stop here
    if (!newEmail || newEmail.trim() === "") return;

    try {
      const token = await auth.currentUser.getIdToken(); 

      const res = await fetch('https://wixapi.onrender.com/admin/convert-to-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          oldEmail: currentEmail, 
          newEmail: newEmail.trim() 
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Conversion failed');

      // Automatically trigger the Firebase password reset email
      await sendPasswordResetEmail(getAuth(), data.newEmail);

      alert(`Success!\n\n1. Changed login email to: ${data.newEmail}\n2. Converted to Email/Password login.\n3. A password reset email has been sent to them automatically!`);
      
    } catch (error) {
      console.error("Conversion error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCustomPasswordReset = async (email) => {
    if (!window.confirm(`Send a custom password reset email to ${email} via SendGrid?`)) return;

    try {
      const token = await auth.currentUser.getIdToken(); 
      const res = await fetch('https://wixapi.onrender.com/admin/send-custom-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');

      alert(`Success! Password reset email sent to ${email} via SendGrid.\n\nIf they still don't get it, here is their secure link to copy and paste to them directly:\n\n${data.resetLink}`);
      
    } catch (error) {
      console.error("Reset error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  function Row({ s }) {
    const active = isActive(s.startDate, s.endDate);
    const isCus = typeof s.id === "string" && s.id.startsWith("cus_");
    return (
      <tr className="border-b border-gray-100 dark:border-gray-800">
        <td className="px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <span>{s.email}</span>
            {s.isAdmin && <span className="px-2 py-0.5 rounded-full text-[11px] bg-purple-100 text-purple-800">Admin</span>}
          </div>
        </td>
        <td className="px-3 py-2 text-sm">{s.type}</td>
        <td className="px-3 py-2 text-sm">{formatMDY(s.startDate)}</td>
        <td className="px-3 py-2 text-sm">{formatMDY(s.endDate)}</td>
        <td className="px-3 py-2 text-sm">{String(s.status || "").toUpperCase()}</td>
        <td className="px-3 py-2 text-sm">{s.auto_renews ? "Yes" : "No"}</td>
        <td className="px-3 py-2 text-sm">
          {s.newsletter_opt_in ? (
            <span className="text-emerald-600 font-bold">Yes</span>
          ) : (
            <span className="text-gray-400">No</span>
          )}
        </td>
        <td className="px-3 py-2 text-sm">{s.referralCode || ""}</td>
        <td className="px-3 py-2 text-sm">{s.referredBy || ""}</td>
        <td className="px-3 py-2 text-sm">{s.referralPaid ? "Yes" : "No"}</td>
        <td className="px-3 py-2 text-sm">{active ? "Active" : "Inactive"}</td>
        <td className="px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <button onClick={() => handleTogglePaid(s)} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-xs">Paid</button>
            <button onClick={() => handleToggleAutoRenews(s)} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-xs">Auto</button>
            {isAdmin && <button onClick={() => sendWelcomeEmail(s)} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs">Welcome</button>}
            {isAdmin && <button onClick={() => hydrateOne(s)} disabled={!isCus || hydratingId === s.id} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs"><FaSyncAlt className={hydratingId===s.id?"animate-spin":""}/></button>}
            {isAdmin && <button onClick={() => openSendImage(s)} className="px-2 py-1 rounded bg-indigo-600 text-white text-xs">Img</button>}
            <button onClick={() => { setEditing(s); setShowModal(true); }} className="px-2 py-1 rounded bg-blue-600 text-white text-xs"><FaEdit /></button>
            <button onClick={() => { setToDelete(s); setDelOpen(true); }} className="px-2 py-1 rounded bg-red-600 text-white text-xs"><FaTrash /></button>
            <button
              onClick={() => handleConvertToEmail(s.email)}
              className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded-md text-xs font-bold transition-colors"
            >
              Convert to Email
            </button>
            <button
              onClick={() => handleCustomPasswordReset(s.email)}
              className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1 rounded-md text-xs font-bold transition-colors"
            >
              Send Custom Reset
            </button>
          </div>
        </td>
      </tr>
    );
  }


  return (
     <div className="p-6 relative z-10 min-h-screen bg-gray-50 dark:bg-gray-900">
             <div className="flex items-center gap-3 mb-4">
          <h1 className="text-3xl font-bold tracking-tight text-blue-900 dark:text-blue-200">Subscribers</h1>

          <button 
            onClick={() => setShowAnnouncementModal(true)}
            className="ml-2 px-3 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white border border-purple-800 shadow-sm flex items-center gap-2"
          >
            <FaPaperPlane /> Preview Announcement
          </button>
          
          <button 
            onClick={() => setShowTosModal(true)}
            className="ml-2 px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white border border-indigo-800 shadow-sm flex items-center gap-2"
          >
            <FaPaperPlane /> Send TOS Update
          </button>

          {/* --- ADD THE BUTTON HERE --- */}
          <button 
            onClick={() => getUsersWithTodayInsight()}
            className="ml-2 px-3 py-1 rounded-lg bg-green-600 hover:bg-green-700 text-white border border-green-800 shadow-sm flex items-center gap-2"
          >
            Test Insight Query
          </button>
          {/* --------------------------- */}
        
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="ml-auto inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-blue-600 text-white"><FaPlus /> Add</button>
        </div>

        
      {/* ... Search filters ... */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <div className="md:col-span-2 flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl border px-3 py-2"><FaSearch className="text-gray-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email" className="flex-1 bg-transparent outline-none" /></div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-white border"><option value="">All types</option><option value="monthly">Monthly</option><option value="annual">Annual</option></select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-white border"><option value="">Any status</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
        <div className="md:col-span-1 flex items-center gap-2"><input value={referralCodeFilter} onChange={(e) => setReferralCodeFilter(e.target.value)} placeholder="Filter by referral code" className={inputNB} /></div>
      </div>

      <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-2xl">
        <table className="min-w-full text-left text-gray-800 dark:text-gray-100">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-sm font-semibold">Email</th>
              <th className="px-3 py-2 text-sm font-semibold">Type</th>
              <th className="px-3 py-2 text-sm font-semibold">Start</th>
              <th className="px-3 py-2 text-sm font-semibold">End</th>
              <th className="px-3 py-2 text-sm font-semibold">Status</th>
              <th className="px-3 py-2 text-sm font-semibold">Auto</th>
              <th className="px-3 py-2 text-sm font-semibold">News</th>
              <th className="px-3 py-2 text-sm font-semibold">Code</th>
              <th className="px-3 py-2 text-sm font-semibold">Referred By</th>
              <th className="px-3 py-2 text-sm font-semibold">Paid</th>
              <th className="px-3 py-2 text-sm font-semibold">Active</th>
              <th className="px-3 py-2 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td className="px-3 py-6 text-center" colSpan={11}>Loading…</td></tr>) : 
             filtered.length === 0 ? (<tr><td className="px-3 py-6 text-center" colSpan={11}>No subscribers</td></tr>) : 
             filtered.map((s) => <Row key={s.id} s={s} />)}
          </tbody>
        </table>
      </div>

      {showDebug && (<div className="mt-6 p-4 rounded-2xl border border-dashed"><pre>{JSON.stringify(probeResults, null, 2)}</pre></div>)}
      {migrateResult && (<div className="mt-4 p-4 rounded-xl border bg-white"><pre className="text-xs">{JSON.stringify(migrateResult.details?.slice(0, 50), null, 2)}</pre></div>)}

      <AddEditSubscriberModal open={showModal} onClose={() => setShowModal(false)} onSaved={async () => { setShowModal(false); await reload(); }} userId={userId} initial={editing} />
      <DeleteModal open={delOpen} onCancel={() => { setDelOpen(false); setToDelete(null); }} onConfirm={async () => { if (!toDelete) return; try { setDeleting(true); await deleteSubscriber(userId, toDelete.id); setDelOpen(false); setToDelete(null); await reload(); } catch (e) { console.error(e); } finally { setDeleting(false); } }} loading={deleting} email={toDelete?.email || ""} />
      <SendImageEmailModal open={sendImgOpen} onClose={() => { setSendImgOpen(false); setImgTarget(null); }} userId={userId} target={imgTarget} />
      
      {/* Register the new modal */}
      <SendTosModal open={showTosModal} onClose={() => setShowTosModal(false)} subscribers={subs} />
      <AnnouncementModal 
  open={showAnnouncementModal} 
  onClose={() => setShowAnnouncementModal(false)} 
/>
    </div>
  );
}